import { useEffect, useState } from 'react'

import { captureSentryError } from '@/lib/sentry'
import { fetchWorkflowConfig, resetWorkflowConfigCache } from '@/lib/workflowConfig'
import { useActionAgentsStore } from '@/store/actionAgentsStore'

interface ActionsAccessResult {
  userId: string
  projectId: string | null
  enabled: boolean
}

/**
 * Server-authoritative Actions access. With a `projectId` the answer is scoped
 * to that project (its organization must hold the grant); without one it is
 * the viewer's user-level access, which decides workspace surfaces such as the
 * Workflows tab. `null` until the answer for this user is in — page gates need
 * that to tell "not yet" from "no".
 */
function useActionsAccessResult(
  userId: string | null | undefined,
  projectId: string | null | undefined,
): ActionsAccessResult | null {
  const [result, setResult] = useState<ActionsAccessResult | null>(null)
  const scopedProjectId = projectId ?? null

  useEffect(() => {
    if (!userId) return
    let cancelled = false

    resetWorkflowConfigCache()

    const load = async () => {
      try {
        const config = await fetchWorkflowConfig(scopedProjectId)
        if (cancelled) return
        setResult({ userId, projectId: scopedProjectId, enabled: config.enabled === true })
        // The backend's accepted agents ride along with an enabled answer.
        if (config.enabled === true && Array.isArray(config.agents))
          useActionAgentsStore.getState().setAgents(config.agents)
      } catch (error) {
        if (cancelled) return
        captureSentryError(error instanceof Error ? error : new Error(String(error)), {
          operation: 'load_actions_access',
        })
        setResult({ userId, projectId: scopedProjectId, enabled: false })
      }
    }

    void load()

    const revalidate = () => {
      if (document.visibilityState === 'visible') void load()
    }
    window.addEventListener('focus', revalidate)
    document.addEventListener('visibilitychange', revalidate)

    return () => {
      cancelled = true
      window.removeEventListener('focus', revalidate)
      document.removeEventListener('visibilitychange', revalidate)
    }
  }, [userId, scopedProjectId])

  if (!userId || result?.userId !== userId || result.projectId !== scopedProjectId) return null
  return result
}

/** User-level Actions access (no project): `null` while loading, then the server's answer. */
export function useActionsAccessStatus(userId: string | null | undefined): boolean | null {
  const result = useActionsAccessResult(userId, null)
  return result ? result.enabled : null
}

/**
 * Actions access for one project: the project's organization must hold the
 * grant, so the answer is scoped to the project being viewed, not just the
 * viewer. `false` until known.
 */
export function useActionsAccess(userId: string | null | undefined, projectId: string | null | undefined): boolean {
  const result = useActionsAccessResult(userId, projectId)
  if (!projectId) return false
  return result?.enabled === true
}
